/**
 * Neigung der Stecknadeln auf einem Plan (Betreiber 28.09.2026: „solche
 * Nadeln … wenn mehrere Nadeln übereinander sind, drehe sie leicht weg“).
 *
 * Die Nadel steckt mit der SPITZE auf der Plankoordinate; die Kugel sitzt am
 * Ende des Stifts. Neigung in Grad: 0 = senkrecht, positiv = Kugel nach
 * rechts. Eine einzelne Nadel steht leicht schraeg wie eine gesteckte Nadel.
 * Nadeln, deren Spitzen naeher als `abstand` beieinander stehen (auch ueber
 * Ketten: A nah B, B nah C), bilden eine Gruppe und faechern sich um die
 * Senkrechte auf — links stehende nach links, rechts stehende nach rechts —
 * damit die Kugeln sich nicht verdecken.
 *
 * Gleiche Rechnung fuer Bildschirm (Pixel) und PDF (mm): `punkte` und
 * `abstand` nur in derselben Einheit uebergeben.
 */

/** Neigung einer einzelnen Nadel (Grad). */
export const NADEL_GRUNDNEIGUNG = 15;
/** Winkel zwischen zwei Nachbarn einer Gruppe (Grad). */
export const NADEL_SPREIZUNG = 40;
/** Staerkste Neigung (Grad) — die Kugel bleibt immer oberhalb der Spitze. */
export const NADEL_MAX_NEIGUNG = 60;

export interface NeigungsOptionen {
    grund?: number;
    spreizung?: number;
    max?: number;
}

/**
 * Neigung je Punkt (gleiche Reihenfolge wie `punkte`). Ungueltige Punkte
 * (keine endliche Zahl) bekommen die Grundneigung und zaehlen zu keiner
 * Gruppe.
 */
export function nadelNeigungen(
    punkte: ReadonlyArray<{ x: number; y: number }>,
    abstand: number,
    optionen: NeigungsOptionen = {},
): number[] {
    const grund = optionen.grund ?? NADEL_GRUNDNEIGUNG;
    const spreizung = optionen.spreizung ?? NADEL_SPREIZUNG;
    const max = optionen.max ?? NADEL_MAX_NEIGUNG;
    const n = punkte.length;
    const gueltig = punkte.map(p => Number.isFinite(p.x) && Number.isFinite(p.y));

    // Gruppen ueber Union-Find (Ketten naher Nadeln gehoeren zusammen).
    const eltern = punkte.map((_, i) => i);
    const wurzel = (i: number): number => {
        while (eltern[i] !== i) { eltern[i] = eltern[eltern[i]]; i = eltern[i]; }
        return i;
    };
    const grenze = Number.isFinite(abstand) && abstand > 0 ? abstand : 0;
    // Raster statt aller Paare (Codex-Review 28.09.): Zellgroesse Abstand/√2,
    // damit zwei Nadeln derselben Zelle IMMER naeher als der Abstand stehen —
    // eine Zelle ist ohne Vergleich eine Gruppe, auch bei vielen Nadeln auf
    // einem Fleck. Zwischen Zellen (5×5-Umfeld deckt den Abstand ab) wird nur
    // gesucht, bis die erste Verbindung steht.
    const vereinen = (a: number, b: number) => {
        const wa = wurzel(a);
        const wb = wurzel(b);
        if (wa !== wb) eltern[wb] = wa;
    };
    if (grenze > 0) {
        const kante = grenze / Math.SQRT2;
        type Zelle = { cx: number; cy: number; mitglieder: number[]; minX: number; maxX: number; minY: number; maxY: number };
        const zellen = new Map<string, Zelle>();
        for (let i = 0; i < n; i++) {
            if (!gueltig[i]) continue;
            const { x, y } = punkte[i];
            const cx = Math.floor(x / kante);
            const cy = Math.floor(y / kante);
            const schluessel = `${cx}|${cy}`;
            const z = zellen.get(schluessel);
            if (z) {
                vereinen(z.mitglieder[0], i);
                z.mitglieder.push(i);
                z.minX = Math.min(z.minX, x); z.maxX = Math.max(z.maxX, x);
                z.minY = Math.min(z.minY, y); z.maxY = Math.max(z.maxY, y);
            } else {
                zellen.set(schluessel, { cx, cy, mitglieder: [i], minX: x, maxX: x, minY: y, maxY: y });
            }
        }
        // Kleinster moeglicher Abstand zweier Zellen ueber die Rahmen ihrer
        // Nadeln: ist schon der zu gross, gibt es kein nahes Paar — dichte,
        // aber getrennte Haufen kosten dann keinen Einzelvergleich.
        const rahmenFern = (a: Zelle, b: Zelle): boolean => {
            const dx = Math.max(0, b.minX - a.maxX, a.minX - b.maxX);
            const dy = Math.max(0, b.minY - a.maxY, a.minY - b.maxY);
            return dx * dx + dy * dy >= grenze * grenze;
        };
        for (const [schluessel, a] of zellen) {
            for (let ox = -2; ox <= 2; ox++) {
                for (let oy = -2; oy <= 2; oy++) {
                    if (ox === 0 && oy === 0) continue;
                    const nachbarSchluessel = `${a.cx + ox}|${a.cy + oy}`;
                    // Jedes Zellpaar nur einmal.
                    if (nachbarSchluessel <= schluessel) continue;
                    const b = zellen.get(nachbarSchluessel);
                    if (!b || wurzel(a.mitglieder[0]) === wurzel(b.mitglieder[0]) || rahmenFern(a, b)) continue;
                    suche: for (const i of a.mitglieder) {
                        for (const j of b.mitglieder) {
                            const dx = punkte[i].x - punkte[j].x;
                            const dy = punkte[i].y - punkte[j].y;
                            if (dx * dx + dy * dy < grenze * grenze) { vereinen(i, j); break suche; }
                        }
                    }
                }
            }
        }
    }

    const gruppen = new Map<number, number[]>();
    for (let i = 0; i < n; i++) {
        if (!gueltig[i]) continue;
        const w = wurzel(i);
        const g = gruppen.get(w);
        if (g) g.push(i); else gruppen.set(w, [i]);
    }

    const neigung = punkte.map(() => grund);
    for (const mitglieder of gruppen.values()) {
        if (mitglieder.length < 2) continue;
        // Links nach rechts (gleiche Spalte: oben zuerst, dann Eingabereihenfolge).
        const sortiert = [...mitglieder].sort((a, b) =>
            (punkte[a].x - punkte[b].x) || (punkte[a].y - punkte[b].y) || (a - b));
        const k = sortiert.length;
        const schritt = Math.min(spreizung, (2 * max) / (k - 1));
        sortiert.forEach((index, rang) => {
            const winkel = (rang - (k - 1) / 2) * schritt;
            neigung[index] = Math.max(-max, Math.min(max, winkel));
        });
    }
    return neigung;
}
